/**
 * Gateway-backed notification service for the authenticated shell.
 *
 * API NORMALIZATION:
 * The notification service is reached through `/notification/...` gateway
 * routes. The browser only sends the normal session token; trusted
 * `x-auth-*` headers are added by the gateway, not by frontend payloads.
 */
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';

export type AppNotificationType = 'stock' | 'transfer' | 'approval' | 'task' | 'system';

export interface AppNotification {
  id: string;
  title: string;
  description: string;
  meta: string;
  type: AppNotificationType;
  read: boolean;
  createdAt?: string;
}

export interface NotificationListQuery {
  page?: number;
  limit?: number;
  channel?: 'push' | string;
}

interface NotificationListResult {
  items: AppNotification[];
  total: number;
  page: number;
  limit: number;
}

/**
 * SERVICE: Notification
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/notification/notifications`;

  /**
   * BUSINESS LOGIC: listNotifications
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  async listNotifications(query: NotificationListQuery = {}): Promise<NotificationListResult> {
    const page = Number(query.page ?? 1) || 1;
    const limit = Number(query.limit ?? 50) || 50;
    const channel = query.channel ?? 'push';
    const response = await firstValueFrom(
      this.http.get<unknown>(this.baseUrl, {
        params: {
          channel,
          page,
          limit
        }
      })
    );
    const items = this.extractItems(response).map((item, index) => this.toNotification(item, index));
    const meta = this.extractMeta(response);

    return {
      items,
      total: meta.total ?? items.length,
      page: meta.page ?? page,
      limit: meta.limit ?? limit
    };
  }

  /**
   * BUSINESS LOGIC: markNotificationAsRead
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  async markNotificationAsRead(id: string): Promise<AppNotification | null> {
    const response = await firstValueFrom(
      this.http.post<unknown>(`${this.baseUrl}/${encodeURIComponent(id)}/read`, null, { withCredentials: true })
    );
    const entity = this.extractEntity(response);

    return entity && this.hasNotificationShape(entity) ? this.toNotification(entity, 0) : null;
  }

  /**
   * BUSINESS LOGIC: markAllAsRead
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  async markAllAsRead(): Promise<void> {
    await firstValueFrom(this.http.post<unknown>(`${this.baseUrl}/read-all`, null, { withCredentials: true }));
  }

  /**
   * BUSINESS LOGIC: extractItems
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private extractItems(response: unknown): Record<string, unknown>[] {
    if (Array.isArray(response)) {
      return response.filter(this.isRecord);
    }

    const root = this.asRecord(response);
    const data = this.asRecord(root?.['data']);
    const candidates = [
      data?.['items'],
      data?.['notifications'],
      data?.['results'],
      root?.['items'],
      root?.['notifications'],
      root?.['results'],
      data
    ];
    const collection = candidates.find(Array.isArray);

    return Array.isArray(collection) ? collection.filter(this.isRecord) : [];
  }

  /**
   * BUSINESS LOGIC: extractEntity
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private extractEntity(response: unknown): Record<string, unknown> | null {
    const root = this.asRecord(response);
    const data = this.asRecord(root?.['data']);

    const notification = this.asRecord(data?.['notification'] ?? root?.['notification']);
    const item = this.asRecord(data?.['item'] ?? root?.['item']);

    return notification ?? item ?? data ?? root;
  }

  private extractMeta(response: unknown): { total?: number; page?: number; limit?: number } {
    const root = this.asRecord(response);
    const data = this.asRecord(root?.['data']);
    const source = data ?? root;

    return {
      total: this.toOptionalNumber(source?.['total'] ?? source?.['totalCount'] ?? source?.['count']),
      page: this.toOptionalNumber(source?.['page']),
      limit: this.toOptionalNumber(source?.['limit'])
    };
  }

  /**
   * BUSINESS LOGIC: toNotification
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private toNotification(raw: Record<string, unknown>, index: number): AppNotification {
    const metadata = this.asRecord(raw['metadata']);
    const rendered = this.asRecord(this.asRecord(raw['template'])?.['rendered'] ?? raw['rendered']);
    const id = this.toText(raw['id'] ?? raw['_id'] ?? raw['notificationId'] ?? `notification-${index + 1}`);
    const title = this.toText(rendered?.['title'] ?? raw['title'] ?? raw['subject'] ?? raw['heading'] ?? raw['name'] ?? 'Notification');
    const description = this.toText(rendered?.['body'] ?? raw['description'] ?? raw['message'] ?? raw['body'] ?? raw['content'] ?? title);
    const createdAt = this.toText(raw['createdAt'] ?? raw['sentAt'] ?? raw['updatedAt']);
    const module = this.toText(metadata?.['module'] ?? raw['module'] ?? raw['category'] ?? raw['channel'] ?? 'Notification');

    return {
      id,
      title,
      description,
      meta: this.buildMeta(module, createdAt),
      type: this.toNotificationType(raw['type'] ?? metadata?.['type'] ?? raw['eventType']),
      read: this.toReadState(raw),
      createdAt
    };
  }

  /**
   * BUSINESS LOGIC: buildMeta
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private buildMeta(module: string, createdAt: string): string {
    if (!createdAt) {
      return module;
    }

    const parsed = new Date(createdAt);

    if (Number.isNaN(parsed.getTime())) {
      return `${module} - ${createdAt}`;
    }

    return `${module} - ${parsed.toLocaleString()}`;
  }

  /**
   * BUSINESS LOGIC: toNotificationType
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private toNotificationType(value: unknown): AppNotificationType {
    const normalized = this.toText(value).toLowerCase();

    if (normalized.includes('stock') || normalized.includes('inventory')) return 'stock';
    if (normalized.includes('transfer')) return 'transfer';
    if (normalized.includes('approval') || normalized.includes('review')) return 'approval';
    if (normalized.includes('task')) return 'task';

    return 'system';
  }

  /**
   * BUSINESS LOGIC: toReadState
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private toReadState(raw: Record<string, unknown>): boolean {
    const explicitRead = raw['read'] ?? raw['isRead'];

    if (typeof explicitRead === 'boolean') {
      return explicitRead;
    }

    if (raw['readAt'] || raw['seenAt']) {
      return true;
    }

    const status = this.toText(raw['status']).toLowerCase();
    return status === 'read' || status === 'seen';
  }

  /**
   * BUSINESS LOGIC: toText
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private toText(value: unknown): string {
    return typeof value === 'string' ? value : value == null ? '' : String(value);
  }

  /**
   * BUSINESS LOGIC: toOptionalNumber
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private toOptionalNumber(value: unknown): number | undefined {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : undefined;
  }

  /**
   * BUSINESS LOGIC: asRecord
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private asRecord(value: unknown): Record<string, unknown> | null {
    return this.isRecord(value) ? value : null;
  }

  /**
   * BUSINESS LOGIC: isRecord
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  /**
   * BUSINESS LOGIC: hasNotificationShape
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private hasNotificationShape(value: Record<string, unknown>): boolean {
    return ['id', '_id', 'notificationId', 'template', 'rendered', 'title', 'subject', 'heading', 'message', 'body'].some((key) => key in value);
  }
}
